import { useCallback, useState } from 'react';

import { api, type CustomizeResponse } from '../../lib/api';

interface State {
  result: CustomizeResponse | null;
  isLoading: boolean;
  error: string | null;
}

const USE_FIXTURES: boolean = String(import.meta.env.VITE_USE_FIXTURES ?? 'false') === 'true';

const PLACEHOLDER_IMAGE =
  'https://images.unsplash.com/photo-1497935586351-b67a49e012bf?w=1024&q=80';

const FIXTURE_PREVIEWS: ReadonlyArray<string> = [
  'https://images.unsplash.com/photo-1497935586351-b67a49e012bf?w=1024&q=80',
  'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=1024&q=80',
  'https://images.unsplash.com/photo-1572442388796-11668a67e53d?w=1024&q=80',
  'https://images.unsplash.com/photo-1563227812-0ea4c22e6cc8?w=1024&q=80',
];

/**
 * Drives the visual customizer flow: collects choices and calls the
 * API. Cache-first behavior is enforced server-side (ADR-0004), so the
 * hook only needs to track in-flight state.
 *
 * In fixtures mode the hook fakes a generation by picking a stable
 * placeholder image based on the choice hash, so the demo shows the
 * cache-key flow without consuming AI credits.
 */
export function useCustomizer(menuItemId: string, baseImageUrl?: string | null): {
  state: State;
  visualize: (customizations: Record<string, string>) => Promise<void>;
  reset: () => void;
} {
  const [state, setState] = useState<State>({ result: null, isLoading: false, error: null });

  const visualize = useCallback(
    async (customizations: Record<string, string>): Promise<void> => {
      setState({ result: null, isLoading: true, error: null });

      if (USE_FIXTURES) {
        // Simulate the generate → cache flow visibly.
        await new Promise((r) => setTimeout(r, 900));
        const hash = hashChoices(customizations);
        const idx = hash % FIXTURE_PREVIEWS.length;
        const fromCache = hash % 3 !== 0; // ~66% mock hit rate, in line with ADR-0004 target
        setState({
          result: {
            assetId: 'fixture-' + hash.toString(36),
            status: fromCache ? 'cached' : 'ready',
            imageUrl: FIXTURE_PREVIEWS[idx] ?? PLACEHOLDER_IMAGE,
            cacheKey: 'fx:' + menuItemId + ':' + hash.toString(16),
            fromCache,
            costUsd: fromCache ? 0 : 0.018,
          },
          isLoading: false,
          error: null,
        });
        return;
      }

      try {
        const result = await api.customize({
          menuItemId,
          baseImageUrl: baseImageUrl ?? PLACEHOLDER_IMAGE,
          customizations,
        });
        setState({ result, isLoading: false, error: null });
      } catch (err) {
        setState({
          result: null,
          isLoading: false,
          error: err instanceof Error ? err.message : 'Visualization failed',
        });
      }
    },
    [menuItemId, baseImageUrl],
  );

  const reset = useCallback((): void => {
    setState({ result: null, isLoading: false, error: null });
  }, []);

  return { state, visualize, reset };
}

function hashChoices(choices: Record<string, string>): number {
  const s = Object.entries(choices)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => k + '=' + v)
    .join('|');
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h;
}
